import { action, computed, observable, reaction } from 'mobx';

import { Category, Template, TemplatesResponse } from '../../../types/api';
import { ApiClient } from '../apiClient/ApiClient';
import { ErrorReporter } from '../errorReporter/ErrorReporter';
import { UiLoggerStore } from './UiLoggerStore';
import { CategoriesStore } from './CategoriesStore';
import { RoutingStore } from './RoutingStore';

export interface ForYouPageStoreState {
  allTemplates: Template[] | null;
  businessTemplates: Template[] | null;
  onlineStoreTemplates: Template[] | null;
  designTemplates: Template[] | null;
  educationTemplates: Template[] | null;
  blogTemplates: Template[] | null;
  blankTemplates: Template[] | null;
}

export interface ForYouPageStoreParams {
  apiClient: ApiClient;
  categoriesStore: CategoriesStore;
  uiLoggerStore: UiLoggerStore;
  routingStore: RoutingStore;
  errorReporter: ErrorReporter;
}

export const TEMPLATES_COUNT_PER_SECTION = 3;

export class ForYouPageStore {
  private readonly apiClient: ApiClient;
  private readonly categoriesStore: CategoriesStore;
  private readonly uiLoggerStore: UiLoggerStore;
  private readonly errorReporter: ErrorReporter;
  private readonly routingStore: RoutingStore;

  @observable public allTemplates: Template[] | null = null;
  @observable public businessTemplates: Template[] | null = null;
  @observable public onlineStoreTemplates: Template[] | null = null;
  @observable public designTemplates: Template[] | null = null;
  @observable public educationTemplates: Template[] | null = null;
  @observable public blogTemplates: Template[] | null = null;
  @observable public blankTemplates: Template[] | null = null;

  constructor(
    { apiClient, categoriesStore, uiLoggerStore, routingStore, errorReporter }: ForYouPageStoreParams,
    initialState?: ForYouPageStoreState,
  ) {
    this.apiClient = apiClient;
    this.categoriesStore = categoriesStore;
    this.uiLoggerStore = uiLoggerStore;
    this.routingStore = routingStore;
    this.errorReporter = errorReporter;

    this.allTemplates = initialState?.allTemplates ?? null;
    this.businessTemplates = initialState?.businessTemplates ?? null;
    this.onlineStoreTemplates = initialState?.onlineStoreTemplates ?? null;
    this.designTemplates = initialState?.designTemplates ?? null;
    this.educationTemplates = initialState?.educationTemplates ?? null;
    this.blogTemplates = initialState?.blogTemplates ?? null;
    this.blankTemplates = initialState?.blankTemplates ?? null;

    this.listenForRouteChanges();
  }

  private listenForRouteChanges() {
    reaction(
      () => this.routingStore.matchedRoute,
      () => this.fetch(),
    );
  }

  public async initialFetch(): Promise<void> {
    await this.fetch();
  }

  private async fetch(): Promise<void> {
    if (this.routingStore.matchedRoute.routeName === 'forYou' && !this.isLoaded) {
      const [
        allTemplates,
        businessTemplates,
        onlineStoreTemplates,
        designTemplates,
        educationTemplates,
        blogTemplates,
        blankTemplates,
      ] = await Promise.all([
        this.fetchTemplatesByCategorySlug('all'),
        this.fetchTemplatesByCategorySlug('business'),
        this.fetchTemplatesByCategorySlug('online-store'),
        this.fetchTemplatesByCategorySlug('design'),
        this.fetchTemplatesByCategorySlug('education'),
        this.fetchTemplatesByCategorySlug('blog'),
        this.fetchTemplatesByCategorySlug('blank'),
      ]);

      this.setTemplates({
        allTemplates,
        businessTemplates,
        onlineStoreTemplates,
        designTemplates,
        educationTemplates,
        blogTemplates,
        blankTemplates,
      });
    }
  }

  @action
  private setTemplates(templates: ForYouPageStoreState): void {
    this.allTemplates = templates.allTemplates;
    this.businessTemplates = templates.businessTemplates;
    this.onlineStoreTemplates = templates.onlineStoreTemplates;
    this.designTemplates = templates.designTemplates;
    this.educationTemplates = templates.educationTemplates;
    this.blogTemplates = templates.blogTemplates;
    this.blankTemplates = templates.blankTemplates;
  }

  private async fetchTemplatesByCategorySlug(categorySlug): Promise<Template[] | null> {
    const categoryIds = this.categoriesStore.getCategoryIds([categorySlug]);

    try {
      const templatesResponse: TemplatesResponse = await this.apiClient.browse({
        offset: 0,
        limit: TEMPLATES_COUNT_PER_SECTION,
        categoryIds,
        templatePageSessionId: this.uiLoggerStore.templatePageSessionId,
        biData: {
          page: 1,
          bookName: this.categoriesStore.bookName,
          category: categorySlug,
          isForYouPage: true,
        },
      });

      return templatesResponse.items;
    } catch (error) {
      this.errorReporter.reportError(error);
      return null;
    }
  }

  @computed
  public get listCategories(): Category[] {
    return [
      'business',
      'blog',
      'online-store',
      'design',
      'portfolio-cv',
      'photography',
      'music',
      'video',
      'creative-arts',
      'landing-pages',
      'events',
      'communities',
      'education',
      'health-wellness',
      'restaurants-food',
      'beauty-and-hair',
      'travel-tourism',
    ].map(this.categoriesStore.getCategoryBySlug);
  }

  @computed
  public get pageTemplates(): Template[] | [] {
    return [].concat(
      ...Object.values(this.serialize())
        .filter(Boolean)
        // converts mobx observableArray to array
        .map((item) => item.slice()),
    );
  }

  @computed
  public get isLoaded(): boolean {
    return Object.values(this.serialize()).every((templates) => templates !== null);
  }

  public serialize(): ForYouPageStoreState {
    return {
      businessTemplates: this.businessTemplates,
      onlineStoreTemplates: this.onlineStoreTemplates,
      designTemplates: this.designTemplates,
      educationTemplates: this.educationTemplates,
      blogTemplates: this.blogTemplates,
      allTemplates: this.allTemplates,
      blankTemplates: this.blankTemplates,
    };
  }
}
